import { Card } from '@/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import {
  CLAIM_REASON_TYPES,
  DATA_TYPE_LABELS,
  EVENT_CATEGORIES,
  EVENT_SOURCE,
  EventCategory,
  EventSource,
  PATH_ROLES,
  PAYMENT_METHODS,
  TaxonomyProperty,
  USER_PROPERTIES,
} from '@/data/taxonomyData'
import { auth } from '@/lib/auth'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, Info, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

export const Route = createFileRoute('/_dashboard/taxonomy')({
  component: TaxonomyPage,
})

type TabType = 'events' | 'userProperties'

function TaxonomyPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabType>('events')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set())
  const [pathRoleModalOpen, setPathRoleModalOpen] = useState(false)
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false)
  const [claimReasonModalOpen, setClaimReasonModalOpen] = useState(false)

  // 인증 확인 — 이 페이지는 shop 데이터를 쓰지 않으므로 shop 조회는 하지 않는다
  // (레이아웃이 이미 인증과 shop 로딩을 보장한다)
  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate({ to: '/' })
    }
  }, [navigate])

  // 아코디언 토글
  const toggleEvent = (eventName: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev)
      if (next.has(eventName)) {
        next.delete(eventName)
      } else {
        next.add(eventName)
      }
      return next
    })
  }

  // 검색 필터된 카테고리
  const filteredCategories = useMemo((): EventCategory[] => {
    if (!searchQuery.trim()) return EVENT_CATEGORIES
    const q = searchQuery.toLowerCase()
    return EVENT_CATEGORIES.map((cat) => ({
      ...cat,
      events: cat.events.filter(
        (event) =>
          event.name.toLowerCase().includes(q) ||
          event.description.toLowerCase().includes(q) ||
          event.properties.some(
            (prop) =>
              prop.name.toLowerCase().includes(q) ||
              prop.description.toLowerCase().includes(q),
          ),
      ),
    })).filter((cat) => cat.events.length > 0)
  }, [searchQuery])

  // 검색 필터된 사용자 속성
  const filteredUserProperties = useMemo(() => {
    if (!searchQuery.trim()) return USER_PROPERTIES
    const q = searchQuery.toLowerCase()
    return USER_PROPERTIES.filter(
      (prop) =>
        prop.name.toLowerCase().includes(q) ||
        prop.description.toLowerCase().includes(q),
    )
  }, [searchQuery])

  const tabs: { key: TabType; label: string }[] = [
    { key: 'events', label: '이벤트' },
    { key: 'userProperties', label: '사용자 속성' },
  ]

  return (
    <>
      {/* max-w-4xl + 가운데 정렬 */}
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* 페이지 헤더 */}
        <div>
          <h2 className="text-lg font-semibold text-on-surface">
            이벤트 택소노미
          </h2>
          <p className="mt-1 text-sm text-on-surface-variant">
            믹스패널에서 수집하는 이벤트, 속성, 사용자 프로필 정보를 확인할 수
            있어요.
          </p>
        </div>

        {/* 탭 + 검색 */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex border-b border-border">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key)
                  setSearchQuery('')
                }}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-on-surface-variant hover:text-on-surface hover:border-border'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-outline-variant" />
            <Input
              placeholder={
                activeTab === 'events'
                  ? '이벤트명 또는 속성명 검색'
                  : '속성명 검색'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* 탭 콘텐츠 */}
        {activeTab === 'events' && (
          <EventsTab
            categories={filteredCategories}
            expandedEvents={expandedEvents}
            onToggle={toggleEvent}
            searchQuery={searchQuery}
            onPathRoleClick={() => setPathRoleModalOpen(true)}
            onPaymentMethodClick={() => setPaymentMethodModalOpen(true)}
            onClaimReasonClick={() => setClaimReasonModalOpen(true)}
          />
        )}
        {activeTab === 'userProperties' && (
          <UserPropertiesTab properties={filteredUserProperties} />
        )}
      </div>

      <PathRoleModal
        open={pathRoleModalOpen}
        onOpenChange={setPathRoleModalOpen}
      />
      <PaymentMethodModal
        open={paymentMethodModalOpen}
        onOpenChange={setPaymentMethodModalOpen}
      />
      <ClaimReasonTypeModal
        open={claimReasonModalOpen}
        onOpenChange={setClaimReasonModalOpen}
      />
    </>
  )
}

// --- 속성 행 렌더링 (그룹 헤더 포함) ---

function PropertyRows({
  properties,
  searchQuery,
  onPathRoleClick,
  onPaymentMethodClick,
  onClaimReasonClick,
}: {
  properties: TaxonomyProperty[]
  searchQuery: string
  onPathRoleClick: () => void
  onPaymentMethodClick: () => void
  onClaimReasonClick: () => void
}) {
  const rows: React.ReactNode[] = []
  let lastGroup: string | undefined

  properties.forEach((prop, idx) => {
    // 새로운 그룹 시작 시 헤더 행 삽입
    if (prop.group && prop.group !== lastGroup) {
      const groupCode = prop.group.split(' — ')[0]
      const groupDesc = prop.group.split(' — ')[1]
      rows.push(
        <TableRow key={`group-${prop.group}`} className="bg-surface-low/70 hover:bg-surface-low/70">
          <TableCell
            colSpan={4}
            className="py-2"
          >
            <code className="font-mono font-medium text-on-surface-variant">
              {groupCode}
            </code>
            <span className="ml-1.5 text-outline-variant">
              — {groupDesc} (상품마다 반복)
            </span>
          </TableCell>
        </TableRow>,
      )
      lastGroup = prop.group
    }
    if (!prop.group) {
      lastGroup = undefined
    }

    const isPathRole = prop.name === 'path_role'
    const isPaymentMethod = prop.name === 'payment_method'
    // claim_reason_type / undone_reason_type 는 동일한 12종 코드 → 모달 공유
    const isClaimReasonType =
      prop.name === 'claim_reason_type' || prop.name === 'undone_reason_type'
    const hasModal = isPathRole || isPaymentMethod || isClaimReasonType
    const isGrouped = !!prop.group

    rows.push(
      <TableRow
        key={`${prop.name}-${idx}`}
        className="border-b border-border last:border-0"
      >
        <TableCell className="py-2.5 align-top">
          {/* 그룹 속성은 · 마커로 소속 표시 */}
          {isGrouped && (
            <span className="text-outline-variant mr-1.5">·</span>
          )}
          {/* 이름은 한 줄로 (줄바꿈 X). 컬럼 폭·min-w로 공간 확보, 좁으면 가로 스크롤 */}
          <code
            className={`text-sm font-mono whitespace-nowrap ${
              searchQuery &&
              prop.name.toLowerCase().includes(searchQuery.toLowerCase())
                ? 'text-yellow-800'
                : 'text-on-surface'
            }`}
          >
            {prop.name}
          </code>
        </TableCell>
        <TableCell className="py-2.5 align-top text-on-surface-variant">{prop.description}</TableCell>
        <TableCell className="py-2.5 align-top text-outline-variant whitespace-nowrap">
          {DATA_TYPE_LABELS[prop.dataType] || prop.dataType}
        </TableCell>
        <TableCell className="py-2.5 align-top text-on-surface-variant">
          {hasModal ? (
            <button
              onClick={
                isPathRole
                  ? onPathRoleClick
                  : isPaymentMethod
                    ? onPaymentMethodClick
                    : onClaimReasonClick
              }
              className="text-primary hover:text-primary underline decoration-dotted"
            >
              {prop.sampleValues} &rarr; 전체 보기
            </button>
          ) : (
            prop.sampleValues && (
              <span className="whitespace-pre-wrap">{prop.sampleValues}</span>
            )
          )}
        </TableCell>
      </TableRow>,
    )
  })

  return <>{rows}</>
}

// --- 데이터 출처 배지 (서버 / 클라이언트 / 광고) ---

const SOURCE_BADGE: Record<EventSource, { label: string; dot: string }> = {
  server: { label: '서버', dot: 'bg-primary' },
  client: { label: '클라이언트', dot: 'bg-outline-variant' },
  ad: { label: '광고', dot: 'bg-tertiary' },
}

// 중성 pill + 작은 컬러 닷 — 비중 낮으면서 한눈에 출처 구분
function SourceBadge({ source }: { source: EventSource }) {
  const { label, dot } = SOURCE_BADGE[source]
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-high px-2 py-0.5 text-xs text-on-surface-variant whitespace-nowrap">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  )
}

// --- 이벤트 탭 ---

function EventsTab({
  categories,
  expandedEvents,
  onToggle,
  searchQuery,
  onPathRoleClick,
  onPaymentMethodClick,
  onClaimReasonClick,
}: {
  categories: EventCategory[]
  expandedEvents: Set<string>
  onToggle: (name: string) => void
  searchQuery: string
  onPathRoleClick: () => void
  onPaymentMethodClick: () => void
  onClaimReasonClick: () => void
}) {
  // 수집 방식 안내 — 클릭 disclosure (접근성: 호버 팝오버 대신)
  const [showSourceHelp, setShowSourceHelp] = useState(false)

  if (categories.length === 0) {
    return (
      <div className="text-center py-12 text-on-surface-variant">
        검색 결과가 없어요.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 수집 방식 안내 — 클릭 disclosure. 행 배지가 우측에 있어 우측 정렬로 연관성 강조 */}
      <div className="flex flex-col items-end px-1">
        <button
          type="button"
          onClick={() => setShowSourceHelp((v) => !v)}
          aria-expanded={showSourceHelp}
          className="inline-flex items-center gap-2 text-xs text-on-surface-variant hover:text-on-surface transition-colors"
        >
          <span className="inline-flex items-center gap-1">
            <Info className="h-3.5 w-3.5" />
            수집 방식
          </span>
          {/* 행에 붙는 배지들을 트리거에도 노출 → 연관성 한눈에 */}
          <span className="inline-flex items-center gap-1">
            <SourceBadge source="server" />
            <SourceBadge source="client" />
            <SourceBadge source="ad" />
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${showSourceHelp ? 'rotate-180' : ''}`}
          />
        </button>
        {showSourceHelp && (
          <div className="mt-2 grid w-fit grid-cols-[auto_1fr] items-center gap-x-2.5 gap-y-2 rounded-lg bg-surface-low px-3 py-2.5 text-xs text-on-surface-variant">
            <SourceBadge source="server" />
            <span>카페24 웹훅을 받아 서버에서 전송하는 이벤트</span>
            <SourceBadge source="client" />
            <span>브라우저 트래커가 수집해 전송하는 이벤트</span>
            <SourceBadge source="ad" />
            <span>광고 플랫폼에서 가져온 캠페인 성과 집계 (유저 행동 아님)</span>
          </div>
        )}
      </div>
      {categories.map((category) => (
        <div key={category.label}>
          {/* 카테고리 라벨 */}
          <h3 className="text-base font-semibold text-on-surface-variant uppercase tracking-wider mb-2 px-1">
            {category.label}
          </h3>

          <Card>
            <div className="divide-y divide-border">
              {category.events.map((event) => {
                const isExpanded = expandedEvents.has(event.name)
                return (
                  <div key={event.name}>
                    {/* 이벤트 행 — 좌: 이름/설명(2줄) · 우: 속성수/배지(2줄 대칭) */}
                    <button
                      onClick={() => onToggle(event.name)}
                      className="w-full px-5 py-3 flex items-start gap-3 text-left hover:bg-surface transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-outline-variant flex-shrink-0 mt-0.5" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-outline-variant flex-shrink-0 mt-0.5" />
                      )}
                      {/* 좌측: 이름 + 설명 */}
                      <div className="flex-1 min-w-0">
                        <code className="text-sm font-mono font-semibold text-primary">
                          {event.name}
                        </code>
                        <p className="text-sm text-on-surface-variant mt-1 leading-relaxed">
                          {event.description}
                        </p>
                      </div>
                      {/* 우측: 속성수(위) + 출처 배지(아래) — 좌측 2줄과 대칭 */}
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        {event.properties.length > 0 && (
                          <span className="text-xs text-outline-variant tabular-nums">
                            속성 {event.properties.length}개
                          </span>
                        )}
                        {EVENT_SOURCE[event.name] && (
                          <SourceBadge source={EVENT_SOURCE[event.name]} />
                        )}
                      </div>
                    </button>

                    {/* 속성 테이블 — 별도 컨테이너 없이 바로 표시 */}
                    {/* 이벤트 속성 테이블 */}
                    {isExpanded && event.properties.length > 0 && (
                      <Table className="text-sm table-fixed min-w-[820px]">
                        <colgroup>
                          <col className="w-[40%]" />
                          <col className="w-[32%]" />
                          <col className="w-[8%]" />
                          <col className="w-[20%]" />
                        </colgroup>
                        <TableHeader>
                          <TableRow className="bg-surface">
                            <TableHead className="py-2">속성명</TableHead>
                            <TableHead className="py-2">설명</TableHead>
                            <TableHead className="py-2 whitespace-nowrap">타입</TableHead>
                            <TableHead className="py-2">샘플값</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <PropertyRows
                            properties={event.properties}
                            searchQuery={searchQuery}
                            onPathRoleClick={onPathRoleClick}
                            onPaymentMethodClick={onPaymentMethodClick}
                            onClaimReasonClick={onClaimReasonClick}
                          />
                        </TableBody>
                      </Table>
                    )}

                    {isExpanded && event.properties.length === 0 && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-outline-variant">
                          별도의 이벤트 속성이 없어요.
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Card>
        </div>
      ))}
    </div>
  )
}

// --- 사용자 속성 탭 (이벤트 속성 테이블과 동일한 스타일) ---

function UserPropertiesTab({
  properties,
}: {
  properties: typeof USER_PROPERTIES
}) {
  return (
    <Card>
      <Table className="text-sm table-fixed min-w-[680px]">
        <colgroup>
          <col className="w-[32%]" />
          <col className="w-[30%]" />
          <col className="w-[8%]" />
          <col className="w-[30%]" />
        </colgroup>
        <TableHeader>
          <TableRow>
            <TableHead className="py-2">속성명</TableHead>
            <TableHead className="py-2">설명</TableHead>
            <TableHead className="py-2 whitespace-nowrap">타입</TableHead>
            <TableHead className="py-2">샘플값</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {properties.map((prop) => (
            <TableRow
              key={prop.name}
              className="border-b border-border last:border-0"
            >
              <TableCell className="py-2.5 align-top">
                <code className="text-sm font-mono whitespace-nowrap text-on-surface">
                  {prop.name}
                </code>
              </TableCell>
              <TableCell className="py-2.5 align-top text-on-surface-variant">{prop.description}</TableCell>
              <TableCell className="py-2.5 align-top text-outline-variant whitespace-nowrap">
                {DATA_TYPE_LABELS[prop.dataType] || prop.dataType}
              </TableCell>
              <TableCell className="py-2.5 align-top text-on-surface-variant">
                {prop.sampleValues}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}

// --- Path Role 모달 ---

function PathRoleModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [search, setSearch] = useState('')

  const filteredRoles = useMemo(() => {
    if (!search.trim()) return PATH_ROLES
    const q = search.toLowerCase()
    return PATH_ROLES.filter(
      (role) =>
        role.pageType.toLowerCase().includes(q) ||
        role.code.toLowerCase().includes(q),
    )
  }, [search])

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl">
      <DialogContent>
        <DialogHeader onClose={() => onOpenChange(false)}>
          <DialogTitle>Path Role 코드 목록</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-on-surface-variant mb-4">
          <code className="font-mono bg-surface-low px-1 rounded">view_page</code>{' '}
          이벤트의{' '}
          <code className="font-mono bg-surface-low px-1 rounded">path_role</code>{' '}
          속성값이에요. 쇼핑몰 페이지 유형을 나타내는 코드입니다.
        </p>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-outline-variant" />
          <Input
            placeholder="페이지유형 또는 코드 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="overflow-y-auto max-h-[50vh]">
          <Table className="text-sm">
            <TableHeader className="sticky top-0 bg-surface-lowest">
              <TableRow>
                <TableHead className="py-2 px-3">페이지유형</TableHead>
                <TableHead className="py-2 px-3">코드</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRoles.map((role) => (
                <TableRow
                  key={role.code}
                  className="border-b border-border last:border-0"
                >
                  <TableCell className="py-2 px-3 text-on-surface">{role.pageType}</TableCell>
                  <TableCell className="py-2 px-3">
                    <code className="text-sm font-mono bg-surface-low text-on-surface px-1.5 py-0.5 rounded">
                      {role.code}
                    </code>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRoles.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={2}
                    className="py-8 text-center text-outline-variant"
                  >
                    검색 결과가 없어요.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// --- 결제수단 모달 ---

function PaymentMethodModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl">
      <DialogContent>
        <DialogHeader onClose={() => onOpenChange(false)}>
          <DialogTitle>결제수단 코드 목록</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-on-surface-variant mb-4">
          <code className="font-mono bg-surface-low px-1 rounded">purchase</code>{' '}
          이벤트의{' '}
          <code className="font-mono bg-surface-low px-1 rounded">payment_method</code>{' '}
          속성값이에요. 카페24 결제수단 코드입니다.
        </p>

        <div className="overflow-y-auto max-h-[50vh]">
          <Table className="text-sm">
            <TableHeader className="sticky top-0 bg-surface-lowest">
              <TableRow>
                <TableHead className="py-2 px-3">코드</TableHead>
                <TableHead className="py-2 px-3">결제수단</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {PAYMENT_METHODS.map((method) => (
                <TableRow
                  key={method.code}
                  className="border-b border-border last:border-0"
                >
                  <TableCell className="py-2 px-3">
                    <code className="text-sm font-mono bg-surface-low text-on-surface px-1.5 py-0.5 rounded">
                      {method.code}
                    </code>
                  </TableCell>
                  <TableCell className="py-2 px-3 text-on-surface">{method.name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// --- 취소/철회 사유 구분 코드 모달 ---

function ClaimReasonTypeModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl">
      <DialogContent>
        <DialogHeader onClose={() => onOpenChange(false)}>
          <DialogTitle>취소/철회 사유 구분 코드 목록</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-on-surface-variant mb-4">
          <code className="font-mono bg-surface-low px-1 rounded">cancel_order</code>{' '}
          이벤트의{' '}
          <code className="font-mono bg-surface-low px-1 rounded">claim_reason_type</code>
          (취소 사유),{' '}
          <code className="font-mono bg-surface-low px-1 rounded">undone_reason_type</code>
          (철회 사유) 속성값이에요. 카페24 취소/철회 사유 구분 코드입니다.
        </p>

        <div className="overflow-y-auto max-h-[50vh]">
          <Table className="text-sm">
            <TableHeader className="sticky top-0 bg-surface-lowest">
              <TableRow>
                <TableHead className="py-2 px-3">코드</TableHead>
                <TableHead className="py-2 px-3">사유</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {CLAIM_REASON_TYPES.map((reason) => (
                <TableRow
                  key={reason.code}
                  className="border-b border-border last:border-0"
                >
                  <TableCell className="py-2 px-3">
                    <code className="text-sm font-mono bg-surface-low text-on-surface px-1.5 py-0.5 rounded">
                      {reason.code}
                    </code>
                  </TableCell>
                  <TableCell className="py-2 px-3 text-on-surface">{reason.name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}

